/**
 * Kopstorm rewards bar
 * ---------------------------------------------------------------------------
 * Keeps the cart drawer milestone bar (free shipping + free gift) in step with
 * the cart without waiting for the drawer to re-render server side.
 *
 * Two things this deliberately does NOT do:
 *
 *  1. It never multiplies the gift threshold by Shopify.currency.rate. That
 *     amount is resolved per market in Liquid and is already denominated in the
 *     visitor's currency. Only the free shipping threshold, which is stored in
 *     the shop's main currency, gets the rate applied, exactly like the theme's
 *     own free-shipping-bar element.
 *  2. It never writes text it did not compute from the config blob, so a value
 *     that cannot be read leaves the server-rendered markup untouched rather
 *     than blanking the bar.
 */
(function () {
  'use strict';

  if (window.customElements.get('kopstorm-rewards-bar')) return;

  class KopstormRewardsBarElement extends HTMLElement {
    constructor() {
      super();
      this._onCartChanged = this._onCartChanged.bind(this);
    }

    connectedCallback() {
      this.config = this._readConfig();
      if (!this.config) return;

      this.rate = (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;

      try {
        this.formatter = new Intl.NumberFormat(
          (window.Shopify && Shopify.locale) || 'nl',
          {
            style: 'currency',
            currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }
        );
      } catch (error) {
        this.formatter = null;
      }

      // A rate other than 1 means the server-rendered shipping threshold is in the
      // wrong currency, so the first paint has to be corrected. On the primary
      // market the rate is 1 and nothing is rewritten at all.
      if (this.rate !== 1) {
        this.render(this.total);
      }

      document.addEventListener('cart:change', this._onCartChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChanged);
    }

    get total() {
      return parseInt(this.getAttribute('data-total'), 10) || 0;
    }

    _readConfig() {
      var node = this.querySelector('[data-kop-rewards-config]');
      if (!node) return null;
      try {
        var parsed = JSON.parse(node.textContent);
        if (!parsed || !parsed.stops || !parsed.stops.length) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    _onCartChanged(event) {
      if (!event || !event.detail || !event.detail.cart) return;
      var total = event.detail.cart.total_price;
      if (typeof total !== 'number') return;
      this.setAttribute('data-total', total);
      this.render(total);
    }

    /** The threshold in the visitor's currency, in cents. */
    _threshold(stop) {
      return stop.rate ? Math.round(stop.cents * this.rate) : stop.cents;
    }

    _money(cents) {
      if (this.formatter) {
        return this.formatter.format(cents / 100);
      }
      return (cents / 100).toFixed(2);
    }

    render(total) {
      var stops = this.config.stops;
      var segment = 100 / stops.length;
      var fill = 0;
      var previous = 0;
      var pending = null;
      var complete = true;

      for (var i = 0; i < stops.length; i++) {
        var threshold = this._threshold(stops[i]);
        var start = i * segment;

        if (total >= threshold) {
          fill = start + segment;
          previous = threshold;
          continue;
        }

        var span = threshold - previous;
        var into = Math.max(total - previous, 0);
        fill = span > 0 ? start + (into / span) * segment : start;
        pending = { type: stops[i].type, missing: threshold - total };
        complete = false;
        break;
      }

      fill = Math.max(0, Math.min(100, fill));

      this.classList.remove('is-awaiting-rate');

      var track = this.querySelector('.kop-rewards__track');
      if (track) track.style.setProperty('--kop-rewards-fill', fill + '%');

      this.classList.toggle('is-complete', complete);

      // Milestone states and, when the rate had to be applied, their labels.
      var nodes = this.querySelectorAll('[data-kop-rewards-stop]');
      for (var n = 0; n < nodes.length && n < stops.length; n++) {
        var stopThreshold = this._threshold(stops[n]);
        nodes[n].classList.toggle('is-reached', total >= stopThreshold);

        if (this.config.showAmounts && stops[n].rate && this.rate !== 1) {
          var amountNode = nodes[n].querySelector('[data-kop-rewards-amount]');
          if (amountNode) amountNode.textContent = this._money(stopThreshold);
        }
      }

      var messageNode = this.querySelector('[data-kop-rewards-message]');
      if (!messageNode) return;

      var template = complete
        ? this.config.messages.done
        : this.config.messages[pending.type];

      if (!template) return;

      messageNode.textContent = complete
        ? template
        : template.split('[amount]').join(this._money(pending.missing));
    }
  }

  window.customElements.define('kopstorm-rewards-bar', KopstormRewardsBarElement);
})();
